import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, Paper, Typography, IconButton, Tooltip, Button, Drawer, FormControl, Select, InputLabel, MenuItem, Autocomplete, TextField } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { withTranslation } from "react-i18next";
import { AmountFormat, CheckAccessFunc, formatDate } from "../../../Components/CommonFunctions/CommonFunctions";
import CommonGridHeader, { CommonGridToolBarWithFilterText, CommonGridToolBarWithFilterTextCustom, CommonPatientDetails } from '../../../Components/Common Components/CommonComponents';
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import ToastMsg from '../../../Components/ToastMsg/ToastMsg';
import Loader from '../../../Components/Loader';
import { ImagePaths } from '../../../Utility/ImagePaths';
import { CurrencySymbol } from "../../../Utility/Constants";
import CreditPaymentPopup from "./CreditPaymentPopup";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { DateTime } from "luxon";
import CommonDatePicker from "../../../Components/Common Components/CommonDatePicker";
import { localGetItem } from "../../../Utility/Services/CacheProviderService";

const InsuranceIndividualPayement = ({ t, history }) => {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(30);
    const [isLoader, setIsLoader] = useState(false);
    const [errorMsg, seterrorMsg] = useState(false);
    const [errorMsgText, seterrorMsgText] = useState('');
    const [successMsg, setsuccessMsg] = useState(false);
    const [successMsgText, setsuccessMsgText] = useState('');
    const [Providerdetails, setProviderDetails] = useState({
        insuranceName: "",
        noOfBills: 0,
        totalClaimAmount: 0,
        totalOutstanding: 0,
        totalPaidAmount: 0,
    })
    const [paymentPopupOpen, setPaymentPopupOpen] = useState(false);
    const [paymentData, setPaymentData] = useState(null);
    const [PatientList, setPatientList] = useState([])
    const [companyData, setcompanyData] = useState(null)
    const [selectedStatus, setselectedStatus] = useState("All");
    const [tpaList, setTpaList] = useState([]);
    const [selectedTpa, setSelectedTpa] = useState(null);
    const [Filter, setFilter] = useState({
        openFilter: false,
        fromDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
        toDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
        dateIndex: 1,
    });
    const appliedFilterRef = useRef(null);

    const TPA_ALL_OPTION = { value: '_all', label: 'All TPAs' };
    const TPA_EMPTY_OPTION = { value: '_empty', label: '(No TPA assigned)' };

    const tpaOptions = useMemo(() => {
        const apiOptions = (tpaList || []).map((item) => ({
            value: item?.tpa_name || '',
            label: item?.tpa_name || '',
            id: item?.id ?? null,
        })).filter((opt) => opt.label);
        return [TPA_ALL_OPTION, TPA_EMPTY_OPTION, ...apiOptions];
    }, [tpaList]);

    useEffect(() => {
        const InsuranceData = history?.location?.state?.companyData;
        const fromDate = history?.location?.state?.fromDate == '' ? "" : history?.location?.state?.fromDate;
        const toDate = history?.location?.state?.toDate == '' ? '' : history?.location?.state?.toDate;
        const dateIndex = history?.location?.state?.dateIndex == 0 ? 0 : history?.location?.state?.dateIndex;
        const incomingTpa = history?.location?.state?.selectedTpa;
        if (incomingTpa) {
            setSelectedTpa(incomingTpa);
        }
        if (InsuranceData) {
            setFilter(prev => ({
                ...prev,
                fromDate: fromDate,
                toDate: toDate,
                dateIndex: dateIndex
            }));
            setTimeout(() => {
                setcompanyData(InsuranceData);
            }, 500);
        }
        getTpaList();
    }, [history]);

    const getTpaList = () => {
        try {
            RestAPIService.getAll(Serviceurls.TPA_List)
                .then((response) => {
                    if (response?.data?.status === "success") {
                        setTpaList(response?.data?.data || []);
                    }
                })
                .catch((error) => {
                    if (error.response?.data?.status === "fail") {
                        errorMessage(error.response?.data?.message);
                    }
                });
        } catch (error) {
            errorMessage(error?.message);
        }
    };

    useEffect(() => {
        if (companyData) {
            getPatientCreditList();
        }
    }, [companyData]);

    const getPatientCreditList = async (fromDate = Filter.fromDate, toDate = Filter.toDate, status = selectedStatus, tpaValue = selectedTpa?.value) => {
        setIsLoader(true);
        try {
            const params = new URLSearchParams();
            params.append('insurance_company_name', companyData?.insurance_company_name || '');
            if (fromDate) params.append('from_date', fromDate);
            if (toDate) params.append('to_date', toDate);
            params.append('status', status === "All" ? "" : status);
            params.append('include_ip', 'true');
            if (tpaValue) params.append('tpa_name', tpaValue);
            const response = await RestAPIService.getAll(`${Serviceurls.INSURANCE_PROVIDER_INFO_GET}?${params.toString()}`);
            if (response.data.status === "success") {
                const list = response.data.data?.map((item, i) => ({ ...item, sno: i + 1 })) || [];
                setProviderDetails(prev => ({
                    ...prev,
                    insuranceName: companyData?.insurance_company_name || "-",
                    noOfBills: response.data?.total_count || 0,
                    totalClaimAmount: response.data?.total_claimed_amount || 0,
                    totalOutstanding: response.data?.total_pending_amount || 0,
                    totalPaidAmount: response.data?.total_received_amount || 0,
                    id: companyData?.id || null,
                }));
                setPatientList(list || []);
            }
        } catch (error) {
            if (error.response?.data?.status === "fail") {
                errorMessage(error.response?.data?.message);
            }
        } finally {
            setIsLoader(false);
        }
    };

    const errorMessage = (msg) => {
        seterrorMsgText(msg)
        seterrorMsg(true)
    };

    const successMessage = (msg) => {
        setsuccessMsgText(msg)
        setsuccessMsg(true)
    };

    const msgClose = () => {
        seterrorMsg(false);
        seterrorMsgText('');
        setsuccessMsg(false);
        setsuccessMsgText('');
    }

    const handleCellClick = (param, event) => {
        event.stopPropagation();
    };

    const handlePaybackClick = (row) => {
        setPaymentData(row);
        setPaymentPopupOpen(true);
    };

    const handleClose = (callAPI = false) => {
        setPaymentPopupOpen(false);
        if (callAPI) {
            getPatientCreditList();
        }
    }

    const getInsuranceApiUrl = (paymentData, insuranceName) => {
        const baseUrl = Serviceurls.INSURANCE_BILL_GET;
        if (paymentData?.ip_admission_id) {
            return `${baseUrl}?ip_admission_id=${paymentData.ip_admission_id}&insurance_company_name=${insuranceName}`;
        } else if (paymentData?.appointment_id) {
            return `${baseUrl}?appointment_id=${paymentData.appointment_id}&insurance_company_name=${insuranceName}`;
        } else if (paymentData?.pharmacy_id && paymentData?.id) {
            return `${baseUrl}?ph_bill_id=${paymentData.id}&insurance_company_name=${insuranceName}`;
        } else if (paymentData?.id) {
            return `${baseUrl}?bill_id=${paymentData?.id}&insurance_company_name=${insuranceName}`;
        }
    };

    const closeFilter = () => {
        if (appliedFilterRef.current) {
            const snap = appliedFilterRef.current;
            setFilter({
                openFilter: false,
                fromDate: snap.fromDate,
                toDate: snap.toDate,
                dateIndex: snap.dateIndex,
            });
            setselectedStatus(snap.status);
            setSelectedTpa(snap.tpa);
        } else {
            setFilter(prev => ({ ...prev, openFilter: false }));
        }
    }

    const FilterDate = (fromDate, toDate, dateIndex) => {
        setFilter(prev => ({
            ...prev,
            fromDate: fromDate,
            toDate: toDate,
            dateIndex: dateIndex
        }));
    }

    const renderFilterScreen = () => {
        const org_id = JSON.parse(localGetItem("org_id"));
        return (
            <Box component="div" className='eMed_rts_filter_container'>
                <Box component="div" className='eMed_filter_Top'>
                    <Typography variant='h6' fontWeight={600}>{t('Filter')}</Typography>
                    <Button
                        emed_tid="vndpay_close"
                        onClick={closeFilter}
                        className='eMed_usrconf_btn'
                    >
                        <img src={ImagePaths.LabCloseButton.default} alt="close" className='eMed_action_img_small' />
                    </Button>
                </Box>

                <Box className='eMed_rts_middle'>
                    <Box className='eMed_date_filter_vendor'>
                        {
                            org_id === 707 ? // without restriction date filter works for kkr alone
                                <CommonDatePicker
                                    FromDate={Filter.fromDate}
                                    ToDate={Filter.toDate}
                                    DateIndex={Filter.dateIndex}
                                    SelectedDates={FilterDate}
                                    HideAllMenu={false}
                                /> :
                                <CommonDatePicker
                                    FromDate={Filter.fromDate}
                                    ToDate={Filter.toDate}
                                    DateIndex={Filter.dateIndex}
                                    SelectedDates={FilterDate}
                                    HideAllMenu={true}
                                    OutOfOffice={true}
                                    Past={true}
                                    Future={false}
                                    FromDateOpen="2026-03-22"   // <-- Dont change this date it will affect the Insurace Reports
                                />}
                    </Box>
                    <FormControl sx={{ width: '100%' }} size="small">
                        <InputLabel id="group-select">{t('Status')}</InputLabel>
                        <Select
                            labelId="group-select"
                            id="demo-simple-select"
                            value={selectedStatus}
                            label={t('Status')}
                            onChange={(event) => setselectedStatus(event.target.value)}
                        >
                            <MenuItem value="All">{'All'}</MenuItem>
                            <MenuItem value="pending">{'Pending'}</MenuItem>
                            <MenuItem value="completed">{"Completed"}</MenuItem>
                        </Select>
                    </FormControl>
                    <Autocomplete
                        size="small"
                        sx={{ width: '100%', marginTop: '0.5vw' }}
                        options={tpaOptions}
                        value={selectedTpa}
                        getOptionLabel={(option) => option?.label || ''}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                        onChange={(_, newValue) => setSelectedTpa(newValue)}
                        renderInput={(params) => <TextField {...params} label={t('TPA')} />}
                    />
                </Box>

                <Stack spacing={2} direction="row" className='eMed_rts_btn' alignItems="center" justifyContent="center">
                    <Button variant='outlined' size="small" emed_tid="fil_clear"
                        onClick={() => {
                            const today = DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd');
                            setFilter({
                                fromDate: today,
                                toDate: today,
                                dateIndex: 1,
                                openFilter: false
                            });
                            setselectedStatus("All");
                            setSelectedTpa(null);
                            appliedFilterRef.current = {
                                fromDate: today,
                                toDate: today,
                                dateIndex: 1,
                                status: "All",
                                tpa: null,
                            };
                            getPatientCreditList(today, today, "All", '');
                        }}
                        id="eMed_Config_btn"
                    >
                        {t("Clear")}
                    </Button>
                    <Button
                        variant='contained'
                        size="small"
                        emed_tid="fil_search"
                        onClick={() => {
                            appliedFilterRef.current = {
                                fromDate: Filter.fromDate,
                                toDate: Filter.toDate,
                                dateIndex: Filter.dateIndex,
                                status: selectedStatus,
                                tpa: selectedTpa,
                            };
                            getPatientCreditList();
                            setFilter(prev => ({ ...prev, openFilter: false }));
                        }}
                        id="eMed_Config_btn"
                    >
                        {t("Search")}
                    </Button>
                </Stack>
            </Box>
        );
    };

    const gridToolBar = () => {
        let TextArray = [
            { label: "From Date", value: formatDate(Filter.fromDate) },
            { label: "To Date", value: formatDate(Filter.toDate) },
        ]
        var ExportData = [];
        PatientList?.length > 0 && PatientList.map((item) => {
            const InsuData = {
                "Patient Name": item?.patient_name,
                "UHID": item?.uhid,
                "Mobile": item?.mobile_no,
                "Admission Details": item?.ip_number || item?.op_number,
                "CCN Number": item?.ccn_number || "-",
                "TPA Name": item?.tpa_name || "-",
                "Bill Amount": item?.net_amount,
                "Claim Amount": item?.claim_amount,
                "Patient Paid": item?.patient_paid,
                "Received Amount": item?.credit_paid,
                "Outsanding Amount": item?.pending_amount,
            };
            ExportData.push(InsuData);
        });
        return (
            <Box>
                <CommonGridToolBarWithFilterTextCustom data={ExportData} title={"Insurance Provider Information"} FilterTextArray={TextArray} filename={"Insurance Provider Info"} />
            </Box>
        )
    }

    const memoizedApiUrl = useMemo(() => {
        const payerName = encodeURIComponent(Providerdetails?.insuranceName || '');
        return getInsuranceApiUrl(paymentData, payerName);
    }, [paymentData, Providerdetails?.insuranceName]);

    const columns = [
        { field: 'sno', headerName: 'S.No', flex: 0.01 },
        {
            field: "patient_name", headerName: 'Patient Details', flex: 0.23,
            renderCell: ({ row }) => (<Box component={'div'}><CommonPatientDetails data={row} disablePhoto={true} />
            </Box>), valueGetter: (params) => {
                const name = params.row?.patient_name || "";
                const mobile = params.row?.mobile_no || "";
                const UHID = params.row?.uhid || "";
                return `${name} ${mobile} ${UHID}`;
            }
        },
        {
            field: 'ip_number', headerName: 'Admission Details', flex: 0.15,
            valueGetter: (params) => {
                const ip = params.row?.ip_number || '';
                const op = params.row?.op_number || '';
                const ccn = params.row?.ccn_number || '';
                return `${ip ? ip : op} ${ccn}`;
            },
            renderCell: ({ row }) => (<Box component={'div'}>
                {row?.op_number ?
                    <Tooltip placement='top' title={row?.op_number} arrow>
                        <Box component={'div'}>{row?.op_number ? '...' + row?.op_number?.slice(-12) : "-"}</Box>
                    </Tooltip> :
                    row?.ip_number ?
                    <Tooltip placement='top' title={row?.ip_number} arrow>
                        <Box component={'div'}>{row?.ip_number ? '...' + row?.ip_number?.slice(-12) : "-"}</Box>
                    </Tooltip> :
                    <Tooltip placement='top' title={row?.invoice_number} arrow>
                        <Box component={'div'}>{row?.invoice_number ? '...' + row?.invoice_number?.slice(-12) : "-"}</Box>
                    </Tooltip>}
                {(row?.op_number || !row?.ccn_number) ? null : <Typography component={'div'} style={{ color: "grey", fontSize: ".8dvw" }}>{`CCN No: ${row?.ccn_number || '-'}`}</Typography>}
            </Box>)
        },
        {
            field: 'tpa_name', headerName: 'TPA Name', flex: 0.13,
            renderCell: ({ row }) => (
                row?.tpa_name?.length > 18 ?
                    <Tooltip placement='top' title={row?.tpa_name} arrow>
                        <Box component={'div'}>{row?.tpa_name?.slice(0, 16) + '...'}</Box>
                    </Tooltip> :
                    <Typography>{row?.tpa_name || '-'}</Typography>
            )
        },
        {
            field: 'net_amount', headerName: `Bill Amount (${CurrencySymbol})`, flex: 0.14,
            renderCell: ({ row }) => AmountFormat(row?.net_amount || 0).replace(CurrencySymbol, "")
        },
        {
            field: 'claim_amount', headerName: `Claim Amount (${CurrencySymbol})`, flex: 0.14,
            renderCell: ({ row }) => AmountFormat(row?.claim_amount || 0).replace(CurrencySymbol, "")
        },
        {
            field: 'patient_paid', headerName: `Patient Paid (${CurrencySymbol})`, flex: 0.14,
            renderCell: ({ row }) => AmountFormat(row?.patient_paid || 0).replace(CurrencySymbol, "")
        },
        {
            field: 'credit_paid', headerName: `Received Amount (${CurrencySymbol})`, flex: 0.14,
            renderCell: ({ row }) => AmountFormat(row?.credit_paid || 0).replace(CurrencySymbol, "")
        },
        {
            field: 'pending_amount', headerName: `Outstanding Amount (${CurrencySymbol})`, flex: 0.16,
            renderCell: ({ row }) => (
                <Typography sx={{ color: +row.pending_amount === 0 ? "#1E972A" : "#B82121" }}>
                    {AmountFormat(row?.pending_amount || 0).replace(CurrencySymbol, "")}
                </Typography>
            )
        },
        {
            field: 'status', headerName: 'Status', flex: 0.09,
            renderCell: ({ row }) => (
                <Typography>{row?.pending_amount == 0 ? "Completed" : "Pending"}</Typography>
            )
        },
        {
            field: 'actions', headerName: 'Action', flex: 0.07,
            renderCell: ({ row }) => (
                <Tooltip title={t("Payback")} placement="top" arrow>
                    <IconButton disabled={row?.pending_amount == 0 || !CheckAccessFunc("Front Office", "Settlement", "Insurance Provider", "Credit Payment", "SubTab")?.editAccess} size="small" sx={{ borderRadius: "0.3vw" }} onClick={() => handlePaybackClick(row)}>
                        <img className="emedhub_vendor_img2" src={ImagePaths.Wallet.default} alt="Wallet" style={{ filter: row?.pending_amount == 0 ? 'invert(20%) sepia(0%) saturate(0%) hue-rotate(180deg) brightness(100%) contrast(100%)' : 'none', opacity: 0.8 }} />
                    </IconButton>
                </Tooltip>
            )
        }
    ];

    return (
        <Box className="emedhub__purchase_page">
            <Paper sx={{ marginBottom: '1vw' }}>
                <Button startIcon={<ArrowBackIosNewIcon />} sx={{ textTransform: "capitalize", margin: "0.25vw" }}
                    variant="outlined" onClick={() => history.push({ pathname: "/CreditSettlement" })}
                >Back To Provider</Button>
            </Paper>
            <Paper>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography style={{ borderBottom: "1px solid #dddddd", fontSize: "0.8dvw", color: "#adadad", padding: "0.7dvw" }}>PROVIDER INFORMATION</Typography>
                    <Tooltip title="Filter" placement='top' arrow>
                        <Button className='eMed_usrconf_btn' onClick={() => {
                            appliedFilterRef.current = {
                                fromDate: Filter.fromDate,
                                toDate: Filter.toDate,
                                dateIndex: Filter.dateIndex,
                                status: selectedStatus,
                                tpa: selectedTpa,
                            };
                            setFilter(prev => ({ ...prev, openFilter: true }));
                        }}>
                            <img src={ImagePaths.Filter.default} alt="filter" className='eMed_action_img' />
                        </Button>
                    </Tooltip>
                </Box>
            </Paper>
            <Paper className="eMed_PharmaVendor_paper" sx={{ height: '5vw' }} elevation={2}>
                <Stack direction="row" sx={{ padding: "1vw" }} spacing={18}>
                    <Stack spacing={1}>
                        <Typography>{t("Insurance Provider")}</Typography>
                        <Tooltip title={t(Providerdetails?.insuranceName || "-")} placement="top" arrow>
                            {
                                Providerdetails?.insuranceName && Providerdetails?.insuranceName?.length > 36 ?
                                    <Typography style={{ fontSize: "1rem", fontWeight: "bold" }}>{(Providerdetails?.insuranceName).slice(0, 35) + "..."}</Typography>
                                    :
                                    <Typography style={{ fontSize: "1rem", fontWeight: "bold" }} noWrap>{Providerdetails?.insuranceName || '-'}</Typography>
                            }
                        </Tooltip>
                    </Stack>
                    <Stack spacing={1}>
                        <Typography>{t("No Of Admissions")}</Typography>
                        <Typography style={{ fontSize: "1rem", fontWeight: "bold" }}>{Providerdetails?.noOfBills || 0}</Typography>
                    </Stack>
                    <Stack spacing={1}>
                        <Typography>{t("Total Claim Amount")}</Typography>
                        <Typography style={{ fontSize: "1rem", fontWeight: "bold" }}>{AmountFormat(Providerdetails?.totalClaimAmount || 0)}</Typography>
                    </Stack>
                    <Stack spacing={1}>
                        <Typography>{t("Received Amount")}</Typography>
                        <Typography style={{ fontSize: "1rem", fontWeight: "bold" }}>{AmountFormat(Providerdetails?.totalPaidAmount || 0)}</Typography>
                    </Stack>
                    <Stack spacing={1}>
                        <Typography>{t("OutStanding Amount")}</Typography>
                        <Typography style={{ fontSize: "1rem", fontWeight: "bold" }}>{AmountFormat(Providerdetails?.totalOutstanding || 0)}</Typography>
                    </Stack>
                </Stack>
            </Paper>
            <Box sx={{ m: '1vw 0vw', height: '58vh', width: '100%' }}>
                <DataGrid
                    rows={PatientList}
                    columns={columns}
                    pageSize={pageSize}
                    page={page}
                    components={{
                        Toolbar: gridToolBar,
                        NoRowsOverlay: () => (
                            <Stack direction="row" justifyContent="center" alignItems="center" sx={{ height: "53vh" }}>
                                {t("NoRecordsFound")}
                            </Stack>
                        )
                    }}
                    localeText={{
                        toolbarColumns: "",
                        toolbarDensity: "",
                        toolbarExport: "",
                        toolbarFilters: "",
                        toolbarExportPrint: ""
                    }}
                    onCellClick={handleCellClick}
                    onRowClick={handleCellClick}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    onPageChange={(newPage) => setPage(newPage)}
                    rowsPerPageOptions={[10, 20, 30]}
                    getRowId={(row) => row.sno}
                    hideFooterSelectedRowCount
                    pagination
                />
            </Box>
            <Drawer
                anchor={'right'}
                open={Filter.openFilter}
                ModalProps={{ onBackdropClick: closeFilter }}
            >
                {renderFilterScreen()}
            </Drawer>
            <Loader loaderOpen={isLoader} />
            <CreditPaymentPopup
                open={paymentPopupOpen}
                onClose={handleClose}
                patientData={paymentData}
                getApiUrl={memoizedApiUrl}
                postApiUrl={Serviceurls.COMMON_CREDIT_PAYMENT_POST}
                type="Insurance credit"
                errorMessage={errorMessage}
                successMessage={successMessage}
                Editaccess={CheckAccessFunc("front_office", "Credit Settlement", "Insurance Provider", null, "Tab")?.editAccess}
            />
            {errorMsg ? <ToastMsg severity='error' msg={errorMsgText} msgPop={msgClose} /> : null}
            {successMsg ? <ToastMsg severity='success' msg={successMsgText} msgPop={msgClose} /> : null}
        </Box>
    );
};

export default withTranslation()(InsuranceIndividualPayement);
